import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { FlexProps } from 'styled-system';
import { flex } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { TrackingEvent } from '../../tracking/getTrackerProps';
import { Button } from '../Button';
import Icon from '../Icon';
import { BodyRegularBR2 } from '../Typography';
import Image from './SuggestionImage';
import { SuggestionsListItemContainer, SuggestionsListItemLink } from './SuggestionsListItem';
import type { SuggestionItem } from './types';
import { trackRemoveRecentSearch, trackSuggestionClick } from './utils/tracking';

interface RecentSearchesListItemProps {
  suggestionsItem: SuggestionItem;
  highlighted: boolean;
  listIndex: number;
  listItems: string[];
  isEditable: boolean;
  onRemove: () => void;
}

const NameContainer = styled(BodyRegularBR2)<FlexProps>`
  ${flex};
`;

const nameContainerStyles: ComponentProps<typeof NameContainer> = {
  as: 'span',
  flex: '1 1 auto',
  color: 'inherit',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  py: 'sp_2',
  ml: 'sp_8',
};

const deleteButtonStyles: ComponentProps<typeof Button> = {
  kind: 'bare',
  color: 'sys.neutral.icon.default',
};

const deleteIconStyles: Partial<ComponentProps<typeof Icon>> = {
  size: 20,
  fill: 'sys.neutral.icon.default',
};

const RecentSearchesListItem = ({
  suggestionsItem,
  highlighted,
  listIndex,
  listItems,
  isEditable,
  onRemove,
  ...props
}: RecentSearchesListItemProps) => {
  const { prefix } = useLocalization();
  const trackingParams = {
    label: suggestionsItem.name,
    position: listIndex,
    length: listItems.length,
    isRecentSearch: true,
  };
  const handleLinkClick = (event: TrackingEvent) => {
    // Preserve link behaviour e.g. to open in new tab
    event.stopPropagation();
    trackSuggestionClick(trackingParams);
  };
  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove();
    trackRemoveRecentSearch(trackingParams);
  };

  return (
    <SuggestionsListItemContainer data-testid="recent-search-item" {...props}>
      <SuggestionsListItemLink
        highlighted={highlighted}
        href={prefix(suggestionsItem.urlEncoded)}
        onClick={handleLinkClick}
      >
        <Image src={suggestionsItem.image} alt="" />
        <NameContainer {...nameContainerStyles}>{suggestionsItem.name}</NameContainer>
        {isEditable && (
          <Button onClick={handleDelete} {...deleteButtonStyles}>
            <Icon name="clear" dataTestId="clear-icon" {...deleteIconStyles} />
          </Button>
        )}
      </SuggestionsListItemLink>
    </SuggestionsListItemContainer>
  );
};

export default RecentSearchesListItem;
